import React from "react";
import parse from "html-react-parser";
import { T } from "@transifex/react";
import {
  CardContainer,
  CardHeader,
  HeaderLeft,
  Title,
  ActionsRow,
  ActionLink,
  ExpandChevron,
  ExpandedBody,
  ReportContent,
} from "./styles";

const DownloadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M22 6l-10 7L2 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface RiskReportCardProps {
  isExpanded: boolean;
  onToggle: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  onEmail?: () => void;
  reportHtml?: string | null;
  className?: string;
  "data-testid"?: string;
}

export function RiskReportCard({
  isExpanded,
  onToggle,
  onDownload,
  onShare,
  onEmail,
  reportHtml,
  className,
  "data-testid": testId,
}: RiskReportCardProps) {
  const handleActionClick = (
    event: React.MouseEvent,
    handler?: () => void
  ) => {
    event.stopPropagation();
    handler?.();
  };

  const handleHeaderKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };

  return (
    <CardContainer
      className={className}
      data-testid={testId || "RiskReportCard"}
    >
      <CardHeader
        onClick={onToggle}
        onKeyDown={handleHeaderKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
      >
        <HeaderLeft>
          <Title data-testid="RiskReportCard__Title">
            <T _str="Your detailed risk assessment" />
          </Title>
          <ActionsRow>
            {onDownload && (
              <ActionLink
                onClick={(e) => handleActionClick(e, onDownload)}
                data-testid="RiskReportCard__DownloadLink"
              >
                <DownloadIcon />
                <T _str="Download" />
              </ActionLink>
            )}
            {onShare && (
              <ActionLink
                onClick={(e) => handleActionClick(e, onShare)}
                data-testid="RiskReportCard__ShareLink"
              >
                <ShareIcon />
                <T _str="Share" />
              </ActionLink>
            )}
            {onEmail && (
              <ActionLink
                onClick={(e) => handleActionClick(e, onEmail)}
                data-testid="RiskReportCard__EmailLink"
              >
                <EmailIcon />
                <T _str="Email" />
              </ActionLink>
            )}
          </ActionsRow>
        </HeaderLeft>
        <ExpandChevron $isExpanded={isExpanded}>
          <ChevronDownIcon />
        </ExpandChevron>
      </CardHeader>

      {isExpanded && reportHtml && (
        <ExpandedBody data-testid="RiskReportCard__Body">
          <ReportContent>{parse(reportHtml)}</ReportContent>
        </ExpandedBody>
      )}
    </CardContainer>
  );
}
